import { graphQlClient } from '@config/graphqlClient';
import ProcessesGraphQL from '@graphql/process.queries';
import { getProcessesNameByIdRequest } from '@models/process.model';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ProcessName {
  name: string;
  processId: string;
}

interface UseProcessNamesReturn {
  processNames: ProcessName[];
  loading: boolean;
  error: string | null;
  getProcessNameById: (processId: string) => string;
}

export const useProcessNames = (processIds: string[]): UseProcessNamesReturn => {
  const [processNames, setProcessNames] = useState<ProcessName[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize the process IDs string to prevent unnecessary re-renders
  const processIdsString = useMemo(() => {
    const validIds = processIds.filter(Boolean);
    return validIds.length > 0 ? validIds.join(',') : '';
  }, [processIds]);

  const fetchProcessNames = useCallback(async (ids: string) => {
    if (!ids) {
      setProcessNames([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: getProcessesNameByIdRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessesNameById,
        { id: ids },
      );

      if (data.getProcessesNameById) {
        setProcessNames(data.getProcessesNameById);
      } else {
        setProcessNames([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch process names');
      setProcessNames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProcessNames(processIdsString);
  }, [processIdsString, fetchProcessNames]);

  const getProcessNameById = useCallback((processId: string): string => {
    if (!processId || processNames.length === 0) return '';
    return processNames.find((process) => process.processId === processId)?.name || '';
  }, [processNames]);

  return {
    processNames,
    loading,
    error,
    getProcessNameById,
  };
};